import { AlertTriangle, Check, Heart, Loader2, MapPin } from "lucide-react";
import { useEffect, useState } from "react";

import { AnswerCompare } from "@/components/AnswerCompare";
import { GroupScores, TotalScore } from "@/components/scores";
import { Shell } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Link } from "@/lib/router";
import { languageLabel } from "@/shared/basics";
import type { MatchSummary } from "@/shared/types";

export function Match({ id, token }: { id: string; token: string }) {
  const [match, setMatch] = useState<MatchSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const base = `/api/match/${id}/${token}`;

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ match: MatchSummary }>(base)
      .then((b) => !cancelled && setMatch(b.match))
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [base]);

  async function accept() {
    setAccepting(true);
    try {
      const b = await api.post<{ match: MatchSummary }>(`${base}/accept`);
      setMatch(b.match);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
    } finally {
      setAccepting(false);
    }
  }

  if (loading) {
    return (
      <Shell>
        <div className="flex min-h-[60vh] items-center justify-center text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" /> Loading…
        </div>
      </Shell>
    );
  }

  if (error || !match) {
    return (
      <Shell>
        <div className="flex min-h-[65vh] flex-col justify-center py-16">
          <div className="grid size-12 place-items-center rounded-2xl border border-destructive/25 bg-destructive/10 text-destructive">
            <AlertTriangle className="size-6" />
          </div>
          <h1 className="mt-6 text-3xl font-semibold tracking-tight">This link isn't valid.</h1>
          <p className="mt-4 max-w-lg text-[15.5px] leading-relaxed text-muted-foreground">
            {error ?? "It may have been superseded, or the other profile may be gone."}
          </p>
          <div className="mt-8">
            <Button asChild className="rounded-full px-6">
              <Link href="/matches">Your matches</Link>
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  const { them } = match;
  const place = [them.locationText, them.countryName].filter(Boolean).join(", ");

  return (
    <Shell
      nav={
        <Button asChild variant="ghost" size="sm" className="rounded-full text-[13px]">
          <Link href="/matches">All matches</Link>
        </Button>
      }
    >
      <div className="py-12">
        <div className="inline-flex items-center gap-2 rounded-full border border-brand/25 bg-brand/8 px-3 py-1 text-[12.5px] text-brand">
          <Heart className="size-3" />
          Matched {new Date(match.createdAt).toLocaleDateString()}
        </div>

        <h1 className="mt-5 text-4xl font-semibold tracking-[-0.03em] sm:text-5xl">
          {them.name}
        </h1>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[14.5px] text-muted-foreground">
          {them.age !== null && <span>{them.age}</span>}
          {place && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="size-3.5" /> {place}
            </span>
          )}
          {them.languages.length > 0 && (
            <span>{them.languages.map(languageLabel).join(", ")}</span>
          )}
          {them.slug && (
            <Link
              href={`/u/${them.slug}`}
              className="underline underline-offset-4 hover:text-foreground"
            >
              Public page
            </Link>
          )}
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-[minmax(0,260px)_1fr] sm:items-start">
          <TotalScore score={match.score} />
          {them.bio ? (
            <div className="rounded-2xl border border-border/70 bg-card/50 p-6 backdrop-blur-sm">
              <div className="mb-3 text-[12px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
                In their words
              </div>
              <p className="whitespace-pre-wrap text-[15.5px] leading-relaxed">{them.bio}</p>
            </div>
          ) : (
            <div className="rounded-2xl border border-border/70 bg-card/50 p-5 backdrop-blur-sm">
              <div className="mb-4 text-[12px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
                Where you agree
              </div>
              <GroupScores groups={match.groups} />
            </div>
          )}
        </div>

        {/* The answers themselves — the thing the decision below is made on. */}
        <div className="mt-8 rounded-2xl border border-border/70 bg-card/50 p-5 backdrop-blur-sm sm:p-6">
          <div className="mb-1 text-[12px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
            What you each said
          </div>
          <p className="mb-5 max-w-2xl text-[13.5px] leading-relaxed text-muted-foreground">
            Every question you and {them.name} both answered, closest first. This is what the
            percentage is made of — read the bottom of the list before you decide, because that's
            where the two of you differ.
          </p>
          <AnswerCompare
            rows={match.comparison}
            groups={match.groups}
            theirName={them.name}
          />
        </div>

        {/* The button */}
        <div className="mt-10">
          {match.contact ? (
            <div className="rounded-3xl border border-brand/30 bg-brand/8 p-7">
              <h2 className="text-2xl font-semibold tracking-tight">You both said yes.</h2>
              <p className="mt-2.5 max-w-xl text-[15px] leading-relaxed text-muted-foreground">
                Here's how {them.name} would like to be reached. They have yours too. That's
                everything we do — the rest is yours.
              </p>
              <pre className="mt-5 whitespace-pre-wrap break-words rounded-2xl border border-brand/20 bg-background/60 p-5 font-sans text-[15.5px] leading-relaxed">
                {match.contact}
              </pre>
            </div>
          ) : match.youAccepted ? (
            <div className="rounded-3xl border border-border/70 bg-card/50 p-7 backdrop-blur-sm">
              <div className="inline-flex items-center gap-2 text-[15px] font-medium text-brand">
                <Check className="size-4" /> You said yes.
              </div>
              <p className="mt-2.5 max-w-xl text-[15px] leading-relaxed text-muted-foreground">
                We won't tell them unless they say yes independently, and we won't tell you what
                they decided unless it's yes. If they do, you'll both get an email with contact
                details. There is nothing else to do here.
              </p>
            </div>
          ) : (
            <div className="rounded-3xl border border-border/70 bg-card/50 p-7 backdrop-blur-sm">
              <h2 className="text-[19px] font-semibold tracking-tight">
                Would you like to take it further?
              </h2>
              <p className="mt-2.5 max-w-xl text-[15px] leading-relaxed text-muted-foreground">
                Nothing is shared until you both say so. If only one of you presses this, the
                other is never told — so there's no cost to being honest either way.
              </p>
              <Button
                onClick={accept}
                disabled={accepting}
                size="lg"
                className="mt-6 rounded-full px-7"
              >
                {accepting ? (
                  <>
                    <Loader2 className="animate-spin" /> Saying yes…
                  </>
                ) : (
                  <>
                    <Heart /> I'm interested
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        <p className="mt-8 text-[13px] leading-relaxed text-muted-foreground">
          {them.name} can see this same list from their side, and it only ever holds questions you
          have both answered — neither of you is reading anything the other hasn't already given.
          Contact details are the one thing still held back, and only both of you pressing that
          button releases them.
        </p>
      </div>
    </Shell>
  );
}
